import { useState } from "react";
import Icon from "../components/Icon";
import { postJSON, reasonOf } from "../contest/api";
import { primaryButton } from "../contest/ui";
import { useSession } from "../session";
import { notifyCopy } from "./cosignCopy";

// Shown in place of the live-contest strip when the producer has nothing
// open to co-sign. A signed-in fan is recorded at their account address, so
// they are never asked to type one.
export default function NotifyPanel({ username }: { username: string }) {
  const session = useSession();
  const signedIn = session.kind !== "visitor";
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ask = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const reply = await postJSON(
      `/v1/web/producers/${encodeURIComponent(username)}/notify`,
      signedIn ? {} : { email: email.trim() },
    );
    setBusy(false);
    if (!reply.ok) {
      setError(reasonOf(reply) ?? notifyCopy.failed);
      return;
    }
    setDone(true);
  };

  return (
    <div data-testid="producer-notify" className="mt-6 max-w-xl rounded-2xl border border-ground-edge bg-ground-raised p-5">
      <div className="flex items-center gap-2">
        <Icon name="notification" className="h-5 w-5 text-gold" />
        <h3 className="font-headline text-xl uppercase leading-none text-chrome">{notifyCopy.heading}</h3>
      </div>
      <p className="mt-3 text-[13px] leading-relaxed text-chrome-dim">{notifyCopy.sub(username)}</p>
      {done ? (
        <p data-testid="producer-notify-done" className="mt-4 text-[13px] font-bold text-money" aria-live="polite">
          {notifyCopy.done(username)}
        </p>
      ) : (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          {!signedIn && (
            <label className="flex-1">
              <span className="sr-only">{notifyCopy.emailLabel}</span>
              <input
                data-testid="producer-notify-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={notifyCopy.placeholder}
                className="h-11 w-full rounded-md border border-ground-edge bg-ground px-3 text-sm text-chrome placeholder:text-chrome-dim focus:border-money focus:outline-none"
              />
            </label>
          )}
          <button
            type="button"
            data-testid="producer-notify-ask"
            onClick={ask}
            disabled={busy || (!signedIn && email.trim() === "")}
            className={`${primaryButton} sm:w-auto disabled:cursor-not-allowed disabled:opacity-60`}
          >
            {busy ? notifyCopy.sending : notifyCopy.action}
          </button>
        </div>
      )}
      {error && (
        <p role="alert" data-testid="producer-notify-error" className="mt-3 text-xs font-bold text-chrome">
          {error}
        </p>
      )}
    </div>
  );
}
